<template>
  <div class="page">
    <div
      v-if="routeFrom && routeFrom.name === 'Data'"
      class="back-link"
      @click="$router.push(routeFrom)"
    >
      <b-icon-chevron-left font-scale="1.3" />
      <span>
        {{ capitalizeFirstLetter($t('words.dataManagement')) }}
      </span>
    </div>
    <div
      v-else
      class="back-link"
      @click="$router.push({ name: 'Datasets' })"
    >
      <b-icon-chevron-left font-scale="1.3" />
      <span>
        {{ $t('addDataset.datasetsList') }}
      </span>
    </div>

    <b-container
      fluid
    >
      <b-overlay
        id="overlay-background"
        :show="loading"
        :variant="'white'"
        :opacity="0.7"
        :blur="'2px'"
        rounded="sm"
        no-wrap
      />

      <h2>{{ $t('addDataset.title') }}</h2>
      <ValidationObserver
        ref="pageFormValidator"
        v-slot="{ errors }"
      >
        <b-row>
          <b-col
            ref="mainFormColumn"
            cols="8"
            class="detail-forms"
          >
            <form>
              <DatasetForm
                ref="datasetForm"
                :mode="'addDataset'"
                :default-form="form"
                :usergroup="form.usergroup"
                @change="setForm"
              />
            </form>
          </b-col>
          <b-col
            ref="sidebarColumn"
            cols="4"
            class="detail-card"
          >
            <div class="side-card-container">
              <div class="card-scroller">
                <DatasetCard
                  :key="`dataset-card-${cardKey}`"
                  :dataset="form"
                  @set="setThumbnail"
                />
                <div class="sidebar-container">
                  <div v-if="!$config.isSaas">
                    <label for="usergroup-search">
                      {{ $t('forms.datasets.organisation.label') }}
                    </label>
                    <SearchUsergroups
                      id="usergroup-search"
                      v-model="form.usergroup"
                      :types="['organisation', 'user-group']"
                      :placeholder="$t('forms.datasets.organisation.help')"
                      :usergroup="form.usergroup"
                      :disabled="false"
                      :reset-after="false"
                      :limit="10"
                      :allow-null="true"
                      @select="setUsergroup"
                      @close="setUsergroup"
                      @remove="setUsergroup"
                    />
                  </div>
                  <div>
                    <label
                      for="record-type-search"
                      :class="isFieldRequired('record_type', formConfig.requiredFields)"
                      style="margin-bottom: 0.5rem;"
                    >
                      {{ $t('datasetDetail.recordType') }}
                    </label>
                    <ValidationProvider
                      v-slot="{ classes, errors }"
                      v-model="form.recordType"
                      :rules="isFieldRequired('record_type', formConfig.requiredFields)"
                      :name="$t('datasetDetail.recordType')"
                    >
                      <div
                        class="control"
                        :class="classes"
                      >
                        <Multiselect
                          id="record-type-search"
                          data-test="dataset-record-type"
                          v-model="form.recordType"
                          :options="datasetsRecordTypes"
                          track-by="choice"
                          label="label"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          :searchable="false"
                          :placeholder="$t('datasetDetail.recordType')"
                        />
                        <span class="form-errors">{{ errors[0] }}</span>
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
                <div class="side-footer">
                  <!-- FORM FOOTER -->
                  <b-button
                    id="create-dataset-with-annexe"
                    variant="primary"
                    data-test="create-dataset"
                    @click="submitCreateDataset"
                  >
                    {{ $t('buttons.save') }}
                  </b-button>
                  <!-- FORM ERRORS -->
                  <div
                    v-if="Object.values(errors).some(el => el.length > 0)"
                    class="error-messages alert alert-danger"
                    role="alert"
                  >
                    <b-icon
                      icon="exclamation-triangle-fill"
                      font-scale="2"
                    />
                    <h5 class="alert-heading">
                      {{ $t('addDataset.error') }}
                    </h5>
                    <ul>
                      <div
                        v-for="(error, field) in errors"
                        :key="field"
                      >
                        <li
                          v-if="error[0]"
                        >
                          <b>{{ field }} :</b> {{ error[0] }}.
                        </li>
                      </div>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </ValidationObserver>
    </b-container>
  </div>
</template>

<script>
import DatasetCard from '@/app/dataset/components/DatasetCard.vue';
import DatasetForm from '@/app/dataset/components/DatasetForm.vue';
import SearchUsergroups from '@/components/Searchbars/SearchUsergroups.vue';
import Multiselect from 'vue-multiselect';

import {
  lookForChangesInContact,
  checkUsergroupJurisdiction,
  contactRoleValue,
  usergroupId
} from '../utils';
import i18n from '@/common/i18n/i18n.js';

import { ValidationObserver, ValidationProvider, extend, configure, setInteractionMode } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

extend('required', {
  ...required,
  message: i18n.messages[i18n.locale].words.requiredField
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});
setInteractionMode('passive');

import { mapState, mapMutations, mapActions } from 'vuex';

import { COVERAGE_OPTIONS } from '@/common/data/constants.js';
import { slugify, validateFormAndScroll } from '@/utils';
import { ErrorService } from '@/services/error-service.js';

import datasetAPI from '@/api/modules/dataset.api.js';
import Resource from '../../../models/resource.js';

export default {
  name: 'NewDatasetView',

  components: {
    DatasetCard,
    DatasetForm,
    Multiselect,
    SearchUsergroups,
    ValidationObserver,
    ValidationProvider,
  },

  data() {
    return {
      routeFrom: null,
      loading: false,
      form: {
        display_name: null,
        description: null,
        keywords: [],
        categories: [],
        topics: [],
        kinds: [],
        recommendation: null,
        restriction: null,
        usergroup: null,
        recordType: null,
        datasetRoles: [],
        license: null,
        copyright: null,
        granularity: null,
        coverage: null,
        frequence: null,
        publicationDate: null,
        lastUpdateDate: null,
        year: null,
        lineage: null,
        resource: null,
        annexe: null,
        thumbnail: null
      },
      selectingScenario: false,
      afterScenarioSelectionTrigger: null,
      selectedScenario: null,
      resourceCreationData: null,
      cardKey: 1
    };
  },

  computed: {
    ...mapState('organisations', [
      'organisationsList'
    ]),
    ...mapState('datasets', [
      'datasetsCategories',
      'datasetsGranularities',
      'datasetsLicences',
      'datasetsTopics',
      'datasetsKinds',
      'datasetsRestrictions',
      'datasetsUpdateFrequencies',
      'lastCreatedDataset',
      'datasetsRecordTypes',
    ]),
    ...mapState('usergroups', [
      'contactsList'
    ]),

    formConfig() {
      return this.$config.forms.dataset;
    },
  },

  async created() {
    this.GET_DATASET_STATIC_DATA();
    this.UPDATE_CURRENT_DATASET_USERGROUP(null);
    if (!this.userData.is_superuser) {
      this.SET_USERGROUPS_FILTERS({
        filter: 'status__in',
        value: 2
      });
      this.SET_USERGROUPS_FILTERS({
        filter: 'user__role__in',
        value: ['1', '2']
      });
    }
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.routeFrom = from;
    });
  },
  beforeRouteLeave(to, from, next) {
    this.RESET_USERGROUPS_FILTERS();
    next();
  },

  methods: {
    ...mapMutations('usergroups', [
      'SET_USERGROUPS_FILTERS',
      'RESET_USERGROUPS_FILTERS'
    ]),
    ...mapMutations('form', [
      'SET_CURRENT_FORM'
    ]),
    ...mapMutations('modal', [
      'CLOSE_MODAL',
    ]),
    ...mapActions('datasets', [
      'GET_DATASET_STATIC_DATA',
      'GET_DATASETS_LIST',
      'CREATE_DATASET',
      'UPDATE_DATASET',
      'UPDATE_CURRENT_DATASET_USERGROUP'
    ]),
    ...mapActions('dashboard', [
      'GET_DASHBOARD_TASKS'
    ]),

    setForm(e) {
      this.form = e;
    },
    setThumbnail(e) {
      this.form.thumbnail = e;
    },

    setUsergroup(e) {
      this.UPDATE_CURRENT_DATASET_USERGROUP(e);
      this.form.usergroup = e;
    },


    getUsergroupForDatasetCreation() {
      if (this.$config.isSaas && this.currentDomainOrganisation && this.currentDomainOrganisation.value) {
        return this.currentDomainOrganisation.value.id;
      } else if (this.form.usergroup) {
        return this.form.usergroup.id;
      } else {
        return '';
      }
    },

    async submitCreateDataset() {
      const isValid = await validateFormAndScroll(this.$refs.pageFormValidator, this, {
        scrollSections: [this.$refs.mainFormColumn, this.$refs.sidebarColumn],
      });
      if (isValid) {
        await this.createDataset();
      }
    },

    async createDataset() {
      try {
        const datasetData = {
          display_name: this.form.display_name,
          codename: slugify(this.form.display_name).substring(0, 100),
          usergroup: this.getUsergroupForDatasetCreation(),
          record_type_value: this.form.recordType ? this.form.recordType.choice : null,
          is_active: true,
          description: this.form.description,
          thumbnail: this.form.thumbnail,
          categories: this.datasetsCategories.filter(el => this.form.categories.includes(el.display_name)),
          topics: this.datasetsTopics.filter(el => this.form.topics.includes(el.display_name)),
          kinds: this.datasetsKinds.filter(el => this.form.kinds.includes(el.id)),
          ...this.form.recommendation && { recommendation: this.form.recommendation },
          ...this.form.restriction && { restriction: this.form.restriction },
          tags: [...this.form.keywords],
          copyright: this.form.copyright,
          ...this.form.publicationDate && {
            publication_date: new Date(this.form.publicationDate).toISOString().split('T')[0]
          },
          ...this.form.lastUpdateDate && {
            last_update_date: new Date(this.form.lastUpdateDate).toISOString().split('T')[0]
          },
          ...this.form.frequence && {
            update_frequency: this.form.frequence.value
          },
          year: this.form.year,
          lineage: this.form.lineage,
          ...this.form.license && { license: this.form.license },
          ...this.form.granularity && { granularity: this.form.granularity },
          ...this.form.coverage && { geographical_coverage: this.form.coverage.code }
        };

        // Check jurisdiction
        const datasetCoverage = COVERAGE_OPTIONS.find(el => el.code === datasetData.geographical_coverage);
        if (datasetCoverage) {        
          const {
            jurisdictionCheck,
            jurisdictionError
          } = await checkUsergroupJurisdiction(
            datasetCoverage,
            datasetData.usergroup,
            null
          );
          const jurisdictionErrorMessage = jurisdictionError ?
            this.$t(
              `messages.error.dataset.${jurisdictionError}`,
              { organisation: this.form.usergroup.display_name }
            ) :
            null;
          if (!jurisdictionCheck && jurisdictionErrorMessage) {
            this.$refs.datasetForm.$refs.geographicalCoverage.applyResult({
              errors: [jurisdictionErrorMessage],
              valid: false,
              failedRules: {}
            });
            ErrorService.onError(null, jurisdictionErrorMessage);
            return;
          }
        }

        // Add extras
        if (
          this.$config.forms.dataset.extraFields &&
          this.$config.forms.dataset.extraFields.length
        ) {
          const extras = [];
          for (const extraField of this.$config.forms.dataset.extraFields) {
            for (const field of extraField.fields) {
              extras.push({
                key: field.key,
                value: this.form[field.key] && this.form[field.key].length ? this.form[field.key] : null
              });
            }
          }
          datasetData.extras = extras;
        }

        this.loading = true;

        await this.CREATE_DATASET(datasetData);
        if (this.lastCreatedDataset) {
          if(
            this.form.datasetRoles.length > 0 &&
            this.form.datasetRoles[this.form.datasetRoles.length - 1].contact.name &&
            this.form.datasetRoles[this.form.datasetRoles.length - 1].contact.email
          ) {
            // eslint-disable-next-line no-unused-vars
            const contactRoles = this.form.datasetRoles.map(({ order, ...el }) => { return el; });
            // Create new contacts
            for (let [i, role] of contactRoles.entries()) {
              if (!role.contact.id) {
                try {
                  role.contact.usergroup_id = usergroupId(role.contact.usergroup);
                  const newContact = await datasetAPI.createContact(role.contact);
                  if (!newContact || !newContact.id) {
                    ErrorService.onError(null, this.$t('messages.error.default'));
                  } else {
                    contactRoles[i].contact.id = newContact.id;
                  }
                } catch (err) {
                  ErrorService.onError(err, undefined, this.$t('messages.error.default'));
                }
              }
            }
            // Check for changed contacts
            for (let role of contactRoles) {
              const originalContact = this.contactsList.find(el => el.id === role.contact.id);
              if (originalContact) {
                if (!lookForChangesInContact(originalContact, role.contact)) {
                  try {
                    await datasetAPI.updateContact(role.contact.id, role.contact);
                  } catch (err) {
                    ErrorService.onError(err, undefined, `${this.$t('messages.error.contact.update')} ${role.contact.name}`);
                  }
                }
              }
            }
            // Create roles
            for (const role of contactRoles) {
              if (!role.contact || !role.contact.id) {
                continue;
              }
              try {
                await datasetAPI.createDatasetRole(this.lastCreatedDataset.id, {
                  role: contactRoleValue(role.role),
                  contact_id: role.contact.id,
                  main: role.main
                });
              } catch (err) {
                ErrorService.onError(err, undefined, this.$t('messages.error.default'));
              }
            }
          }
          if (this.lastCreatedDataset.id) {
            try {
              const data = {
                ...this.lastCreatedDataset,
                record_type_value:this.lastCreatedDataset.record_type.id,
                usergroup: this.lastCreatedDataset.usergroup.id
              };
              await this.UPDATE_DATASET({
                id: this.lastCreatedDataset.id,
                data: data
              });
              this.goToCreatedDataset();
            } catch {
              this.goToCreatedDataset();
            }
          }
        } else {
          this.loading = false;
        }
      } catch(err) {
        console.error(err);
        this.loading = false;
      }
    },

   
    // Fired after each resource creation
    afterCreationRequests() {
      this.GET_DATASETS_LIST({
        direction: null,
        field: null
      });
      this.SET_CURRENT_FORM({
        id: null,
        form: null
      });
      this.GET_DASHBOARD_TASKS({
        direction: null,
        field: null
      })
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });

      if (this.lastCreatedDataset.id) {
        this.goToCreatedDataset();
      }
    },

    goToCreatedDataset() {
      this.CLOSE_MODAL('progress');
      this.$router.push({ name: 'DatasetDetail', params: { id: this.lastCreatedDataset.id } });
    },
  }
};
</script>

<style lang="less" scoped>
.add-dataset-form {
  margin-left: max(2em, auto);
  margin-right: max(2em, auto);
  form {
    max-width: 1000px;
  }
}

h2 {
  color: @blue;
  text-shadow: 0 0 2px @lightBlue;
  margin-top: 0.8em;
  margin-left: 0.5em;
}

.sidebar-container {
  width: 80%;
  margin: 0 10% 1em;
  label {
    margin-top: 0.75rem;
  }
}

.detail-forms,
.detail-card {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

.detail-forms {
  overflow-x: hidden;
}

</style>
